<script setup lang="ts">
import type { ApalumaChatThread } from '@/types/api-response/chat-session-types.ts';
import { Icon } from '@iconify/vue';
import { ref, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import DangerButton from '@/components/button/danger-button.vue';
import DarkButton from '@/components/button/dark-button.vue';

const props = defineProps<{
   session: ApalumaChatThread | null;
   error?: string | null;
}>();

const emit = defineEmits<{
   delete: [];
}>();

const modelValue = defineModel<boolean>({ required: true, default: false });
const isDeleting = ref(false);

// Reset loading state when modal closes
watch(modelValue, (isOpen) => {
   if (!isOpen) {
      isDeleting.value = false;
   }
});

// Reset loading state when an error occurs
watch(() => props.error, (error) => {
   if (error) {
      isDeleting.value = false;
   }
});

async function handleDelete() {
   isDeleting.value = true;
   emit('delete');
}

function handleCancel() {
   if (!isDeleting.value) {
      modelValue.value = false;
   }
}

function handleBackdropClick(e: MouseEvent) {
   if (e.target === e.currentTarget && !isDeleting.value) {
      handleCancel();
   }
}
</script>

<template>
   <Teleport to="body">
      <Transition
         enter-active-class="duration-300 ease-in"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="duration-200 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="modelValue"
            class="fixed inset-0 z-[9999]"
         >
            <!-- Backdrop -->
            <div class="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" @click="handleBackdropClick" />

            <!-- Modal Content -->
            <div class="flex min-h-full items-center justify-center p-4">
               <Transition
                  enter-active-class="duration-300 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="duration-200 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
               >
                  <div
                     v-if="modelValue"
                     class="relative w-full max-w-xl transform overflow-hidden rounded-2xl bg-white shadow-xl px-6 py-8 border-2 border-black"
                     @click.stop
                  >
                     <!-- Close button -->
                     <button
                        class="absolute top-4 right-4 size-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="isDeleting"
                        aria-label="Close modal"
                        @click="handleCancel"
                     >
                        <Icon :icon="IconLib.actions.close" class="size-5 text-gray-600" />
                     </button>

                     <h1 class="font-semibold text-3xl text-center">
                        Delete Chat
                     </h1>

                     <h3 class="text-center text-gray-500 mt-4 text-xl text-[#1C1C1C]/80">
                        {{ isDeleting ? 'Deleting chat...' : 'This action cannot be undone' }}
                     </h3>

                     <!-- Error message -->
                     <div
                        v-if="error"
                        class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                     >
                        <div class="flex items-center gap-2 text-red-600">
                           <Icon :icon="IconLib.error.circle" class="size-5 flex-shrink-0" />
                           <p class="text-sm font-medium">
                              {{ error }}
                           </p>
                        </div>
                     </div>

                     <div class="flex items-center justify-center gap-4 mt-4">
                        <DarkButton
                           variant="outline"
                           size="sm"
                           stretch="lg"
                           :disabled="isDeleting"
                           @click="handleCancel"
                        >
                           Cancel
                        </DarkButton>
                        <DangerButton
                           variant="solid"
                           size="sm"
                           stretch="lg"
                           :disabled="isDeleting"
                           @click="handleDelete"
                        >
                           <div class="flex items-center gap-2">
                              <Icon
                                 v-if="isDeleting"
                                 :icon="IconLib.loading.spinner"
                                 class="size-4 animate-spin"
                              />
                              <span>{{ isDeleting ? 'Deleting...' : 'Delete' }}</span>
                           </div>
                        </DangerButton>
                     </div>
                  </div>
               </Transition>
            </div>
         </div>
      </Transition>
   </Teleport>
</template>

<style scoped>

</style>
